import type { AnswerQueue } from '../model/offline';
import { api } from '../../../lib/api';
import { isExpired, pollDelayMs, remainingMs, shouldRequestSubmit } from '../../exam-guard';
import type { AttemptView } from '../../../lib/api-types';
import { isLastUnit } from '../model/palette';
import { useEffect, useRef } from 'react';

/**
 * Time left for the open part, and what to do at zero: save and let the server lock or advance
 * the part, and for an SSC Rankers League attempt keep asking until it has auto-submitted.
 */
export function usePlayerClock(args: {
  attemptId: string;
  view: AttemptView | null;
  now: number;
  offset: number;
  managed: boolean;
  queue: AnswerQueue;
  load: () => Promise<void>;
}) {
  const { attemptId, view, now, offset, managed, queue, load } = args;
  const expiredUnit = useRef<number | null>(null);
  const remainingRef = useRef(0);

  const remaining = view ? remainingMs(view, now, offset) : 0;
  useEffect(() => {
    remainingRef.current = remaining;
  });

  // Time up for this part: save what we have and let the server lock/advance or submit.
  // Once per part; if the server still says "running" (clock skew) the 60 s resync catches up.
  const expired = !!view && view.status === 'in_progress' && isExpired(remaining);
  const unitIdx = view?.unitIdx ?? -1;
  useEffect(() => {
    if (!expired || expiredUnit.current === unitIdx) return;
    expiredUnit.current = unitIdx;
    const t1 = setTimeout(() => void queue.flush().then(load), 1500);
    return () => clearTimeout(t1);
  }, [expired, unitIdx, queue, load]);

  // SSC Rankers League: after time up, keep asking until the server has auto-submitted. If the server
  // is still late 20 s past the deadline on the last part, ask it once to submit.
  const statusNow = view?.status;
  const lastPartNow = !!view && isLastUnit(view);
  useEffect(() => {
    if (!managed || !expired || statusNow !== 'in_progress') return;
    let stop = false;
    let n = 0;
    let asked = false;
    let id: ReturnType<typeof setTimeout> | undefined;
    const step = () => {
      id = setTimeout(() => {
        void (async () => {
          await queue.flush();
          if (shouldRequestSubmit(remainingRef.current, 'in_progress', asked, lastPartNow)) {
            asked = true;
            try {
              await api('POST', `/app/attempts/${attemptId}/submit`);
            } catch {
              /* the server decides; keep polling */
            }
          }
          await load();
          n += 1;
          if (!stop) step();
        })();
      }, pollDelayMs(n));
    };
    step();
    return () => {
      stop = true;
      if (id) clearTimeout(id);
    };
  }, [managed, expired, statusNow, lastPartNow, attemptId, queue, load]);

  return { remaining, expired };
}
